import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ExternalLink, TriangleAlert } from "lucide-react";
import { CopyButton } from "~/components/assistant/CopyButton";
import { Card, CardContent, CardHeader, CardTitle } from "~/components/ui/card";
import { Input } from "~/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import { Skeleton } from "~/components/ui/skeleton";
import { TableBody, TableCell, TableHead, TableHeader, TableRow } from "~/components/ui/table";
import useNextGenHeader from "~/contexts/nextgen-header";
import { useDocumentTitle } from "~/hooks/useDocumentTitle";
import { buildEntityBreadcrumbs } from "~/pages/manifold-nextgen/breadcrumbs";
import { ResourceLoadError } from "~/pages/manifold-nextgen/components/ResourceLoadError";
import { Stat } from "~/pages/manifold-nextgen/components/Stat";
import { AdminPage } from "~/pages/manifold-nextgen/components/admin/AdminPage";
import {
  nextgenAdminConversationPath,
  nextgenAdminEvalsPath,
} from "~/pages/manifold-nextgen/paths";
import { EVAL_SCORE_MAX } from "~/services/evals";
import { CaseResultDrawer } from "./CaseResultDrawer";
import { CaseNameCell, EvalTable, SortableHead } from "./EvalTable";
import { RunComparisonCard } from "./RunComparisonCard";
import { RunConfigCard } from "./RunConfigCard";
import { RunReportCard } from "./RunReportCard";
import { useTableSort } from "./TableSort";
import { ALL_FILTER } from "./constants/runs";
import { CaseOutcome } from "./constants/scoring";
import { RESULT_SORT_DIRECTIONS, ResultSortKey } from "./constants/sorting";
import { OutcomeBadge, RunStatusBadge } from "./primitives/badges";
import { TagChips } from "./primitives/chips";
import { ScoreValue } from "./primitives/figures";
import { formatDuration, formatEvalTimestamp, shortModelName } from "./primitives/format";
import {
  parseApiTimestamp,
  parseTriggeredBy,
  resultDurationSeconds,
  runDurationSeconds,
} from "./selectors/core";
import { buildResultRows, sortResultRows } from "./selectors/results";
import { summarizeRun } from "./selectors/runs";
import {
  useEvalCases,
  useEvalRun,
  useEvalRunResults,
  useEvalRuns,
  useRunAgentLabels,
} from "./useEvalApi";

const OUTCOME_FILTER_OPTIONS = [
  { value: ALL_FILTER, label: "All outcomes" },
  { value: CaseOutcome.PASSED, label: "Passed" },
  { value: CaseOutcome.FAILED, label: "Failed" },
  { value: CaseOutcome.ERRORED, label: "Errored" },
] as const;

/**
 * One run in full: what it was configured to do, how it scored, and every case it
 * executed. Reached from several places, so it has to stand alone — which is why
 * the configuration is up top rather than only the numbers.
 */
/** The hairline separator between the identity line's facts. */
const Dot = () => (
  <span className="text-muted-foreground/50" aria-hidden>
    &middot;
  </span>
);

const EvalRunDetailPage = () => {
  const { runId = "" } = useParams<{ runId: string }>();
  const { data: run, error: runError, isLoading: runLoading } = useEvalRun(runId || null);
  const { data: resultsPage, isLoading: resultsLoading } = useEvalRunResults(runId || null);
  // Unfiltered: a run's cases can carry any tags, so the page needs the whole
  // case list to name them, not the tag-filtered slice the dashboard was on.
  const { data: casesPage } = useEvalCases();
  // Also unfiltered — a baseline can be any earlier run, including one whose
  // tags do not match this one's.
  const { data: runsPage } = useEvalRuns();

  const [query, setQuery] = useState("");
  const [outcomeFilter, setOutcomeFilter] = useState<string>(ALL_FILTER);
  const sort = useTableSort<ResultSortKey>(ResultSortKey.OUTCOME, "asc", RESULT_SORT_DIRECTIONS);
  const [openResultId, setOpenResultId] = useState<string | null>(null);

  const runLabel = run ? formatEvalTimestamp(parseApiTimestamp(run.started_at)) : undefined;

  useDocumentTitle(runLabel ? `Eval run · ${runLabel}` : "Eval run");
  // In the app header, the way the rest of nextgen puts a detail page there. The
  // root crumb names the section rather than saying "All", which under the Admin
  // nav picker does not say all of what. The header sits outside the scrolling
  // region, so the trailing crumb still names the run once the page is scrolled.
  const headerBreadcrumbs = useMemo(
    () =>
      buildEntityBreadcrumbs({
        rootHref: nextgenAdminEvalsPath(),
        rootLabel: "Evals",
        entityName: runLabel,
      }),
    [runLabel]
  );

  useNextGenHeader({ breadcrumbs: headerBreadcrumbs });
  // Every run on the page at once — this one plus every baseline candidate — so
  // the comparison card can name the pair it is comparing without a second read.
  const agentLabels = useRunAgentLabels([
    run?.agent_id ?? null,
    ...(runsPage?.items ?? []).map((candidate) => candidate.agent_id),
  ]);

  const results = resultsPage?.items ?? [];
  const summary = summarizeRun(results);
  const rows = buildResultRows(results, casesPage?.items ?? []);
  // With `keep_conversations` off, every row's transcript cell reads the same,
  // so the column carries nothing a reviewer can act on. The card's subtitle
  // says it once instead.
  const keptAnyConversation = results.some((result) => result.conversation_id != null);

  const search = query.trim().toLowerCase();
  const filtered = rows.filter((row) => {
    if (outcomeFilter !== ALL_FILTER && row.outcome !== outcomeFilter) {
      return false;
    }
    if (!search) {
      return true;
    }
    return (row.evalCase?.name ?? row.result.case_id).toLowerCase().includes(search);
  });
  const sorted = sortResultRows(filtered, sort.sortKey, sort.direction);

  const openRow = sorted.find((row) => row.result.id === openResultId) ?? null;
  const runErrorMessage = typeof run?.report?.error === "string" ? run.report.error : null;

  if (runLoading && !run) {
    // Not inside `AdminPage`: the title *is* the run's start time, so there is
    // nothing to head the frame with until the run arrives.
    return (
      <div className="flex flex-col gap-6 p-6" role="status" aria-label="Loading eval run">
        <Skeleton className="h-9 w-64" />
        <Skeleton className="h-28 rounded-lg" />
        <Skeleton className="h-64 rounded-lg" />
      </div>
    );
  }

  if (runError || !run) {
    return <ResourceLoadError error={runError} resourceLabel="eval run" className="py-24" />;
  }

  const { trigger, user } = parseTriggeredBy(run.triggered_by);

  return (
    <AdminPage
      title={formatEvalTimestamp(parseApiTimestamp(run.started_at))}
      titleAdornment={<RunStatusBadge status={run.status} />}
      width="default"
      /* A run's title can only be its timestamp, which says nothing about which
         run it is. The identity — what it selected, what it asked, who started
         it — is this page's byline, rather than three cards down. Phrasing
         content only and a step down from the frame's byline size: this is a
         dense strip of facts, not a tagline. */
      description={
        <span className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm">
          <TagChips tags={run.tags} emptyLabel="All cases" />
          <Dot />
          {run.agent_id ? (
            <span
              className={agentLabels.isNamed(run.agent_id) ? "font-medium text-foreground" : ""}
              title={run.agent_id}
            >
              {agentLabels.agentLabel(run.agent_id)}
            </span>
          ) : (
            <span>No agent recorded</span>
          )}
          <Dot />
          {run.model_name ? (
            <span className="font-mono text-xs" title={run.model_name}>
              {shortModelName(run.model_name)}
            </span>
          ) : (
            <span>Model not yet recorded</span>
          )}
          {(trigger || user) && (
            <>
              <Dot />
              <span>
                {trigger && <span>{trigger}</span>}
                {trigger && user && ", "}
                {user}
              </span>
            </>
          )}
          <Dot />
          <span className="flex items-center gap-1">
            <span className="max-w-[13ch] truncate font-mono text-xs">{run.id}</span>
            <CopyButton text={run.id} variant="ghost" size="sm" />
          </span>
        </span>
      }
    >
      {runErrorMessage && (
        <div className="rounded-md border border-red-300 bg-red-50 p-3">
          <h2 className="flex items-center gap-1.5 text-sm font-semibold text-red-700">
            <TriangleAlert className="size-4" aria-hidden />
            The run failed
          </h2>
          <p className="mt-1 text-xs break-words text-red-700">{runErrorMessage}</p>
        </div>
      )}

      {/* The same tiles the dashboard leads with, because these are the same
            kind of statement: the figures worth reading first. */}
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <Stat
          label="Mean score"
          value={summary.meanScore == null ? "—" : summary.meanScore.toFixed(2)}
          unit={summary.meanScore == null ? undefined : ` / ${EVAL_SCORE_MAX}`}
          detail="Per case, iterations averaged"
        />
        <Stat
          label="Pass rate"
          value={summary.passRate == null ? "—" : `${Math.round(summary.passRate * 100)}%`}
          detail={
            summary.judged === 0
              ? "Nothing judged"
              : `${summary.passed} of ${summary.judged} judged iterations`
          }
        />
        <Stat
          label="Cases"
          value={String(summary.distinctCaseCount)}
          detail={
            summary.resultCount > summary.distinctCaseCount
              ? `${summary.resultCount} iterations`
              : "Once each"
          }
        />
        <Stat
          label="Duration"
          value={formatDuration(runDurationSeconds(run))}
          detail="Wall clock, start to finish"
        />
      </div>

      <RunConfigCard
        run={run}
        observedCaseCount={summary.distinctCaseCount}
        observedResultCount={summary.resultCount}
      />

      <Card>
        <CardHeader className="flex flex-row flex-wrap items-start justify-between gap-3 pb-4">
          <div>
            <CardTitle className="text-base font-semibold">Case results</CardTitle>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search cases…"
              className="w-[200px]"
              aria-label="Search case results"
            />
            <Select value={outcomeFilter} onValueChange={setOutcomeFilter}>
              <SelectTrigger className="w-[150px]" aria-label="Filter by outcome">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {OUTCOME_FILTER_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent>
          {resultsLoading && results.length === 0 ? (
            <Skeleton className="h-48 rounded-lg" />
          ) : sorted.length === 0 ? (
            <p className="py-12 text-center text-sm text-muted-foreground">
              {results.length === 0
                ? "This run has posted no results yet."
                : "No case results match this filter."}
            </p>
          ) : (
            <EvalTable>
              <TableHeader>
                <TableRow>
                  <SortableHead columnKey={ResultSortKey.CASE} label="Case" sort={sort} />
                  {/* Unsorted: ordering every case's iterations against each
                        other says nothing, and the column is here to tell two
                        otherwise identical rows apart. */}
                  <TableHead className="w-28">Iteration</TableHead>
                  <SortableHead columnKey={ResultSortKey.OUTCOME} label="Outcome" sort={sort} />
                  <SortableHead
                    columnKey={ResultSortKey.SCORE}
                    label="Score"
                    alignRight
                    sort={sort}
                  />
                  {/* The case's own time, not the run's. Sortable because the
                        question it answers is which case is the slow one, which
                        needs the column ordered rather than read row by row. */}
                  <SortableHead
                    columnKey={ResultSortKey.DURATION}
                    label="Time"
                    alignRight
                    sort={sort}
                  />
                  {keptAnyConversation && <TableHead className="w-24">Transcript</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {sorted.map((row) => (
                  <TableRow key={row.result.id} className="group">
                    {/* The same case-identity column the dashboard's tables
                          use, so a case reads the same wherever it appears. */}
                    <TableCell className="max-w-[420px]">
                      <CaseNameCell
                        name={row.evalCase?.name ?? row.result.case_id}
                        description={row.evalCase?.description ?? null}
                        onSelect={() => setOpenResultId(row.result.id)}
                      />
                    </TableCell>
                    <TableCell className="text-xs tabular-nums text-muted-foreground">
                      {row.siblingResultIds.length > 1
                        ? `${row.result.iteration + 1} of ${row.siblingResultIds.length}`
                        : "Once"}
                    </TableCell>
                    <TableCell>
                      <OutcomeBadge outcome={row.outcome} />
                    </TableCell>
                    <TableCell className="text-right text-xs">
                      <ScoreValue score={row.result.score} />
                    </TableCell>
                    <TableCell className="text-right text-xs tabular-nums text-muted-foreground">
                      {/* `formatDuration` renders an em dash for a result the
                            runner recorded no timing for. */}
                      {formatDuration(resultDurationSeconds(row.result))}
                    </TableCell>
                    {keptAnyConversation && (
                      <TableCell>
                        {row.result.conversation_id ? (
                          <Link
                            to={nextgenAdminConversationPath(row.result.conversation_id)}
                            className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
                          >
                            Open
                            <ExternalLink className="size-3" aria-hidden />
                          </Link>
                        ) : (
                          <span className="text-xs text-muted-foreground">&mdash;</span>
                        )}
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </EvalTable>
          )}
        </CardContent>
      </Card>

      <RunComparisonCard
        run={run}
        agentLabels={agentLabels}
        results={results}
        cases={casesPage?.items ?? []}
        allRuns={runsPage?.items ?? []}
      />

      <RunReportCard run={run} />

      <CaseResultDrawer
        open={openRow != null}
        onOpenChange={(open) => !open && setOpenResultId(null)}
        runId={runId}
        resultId={openRow?.result.id ?? null}
        caseId={openRow?.result.case_id ?? null}
        siblingResultIds={openRow?.siblingResultIds ?? []}
        onSelectResult={setOpenResultId}
      />
    </AdminPage>
  );
};

export default EvalRunDetailPage;
